import React, { useState, useEffect, ChangeEvent, MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './TemplateEditor.css';
import { WelcomeSlideConfig, FinalSlideConfig, TemplateConfig, TemplateApiResponse  } from '../types';
import SlidePreview from './SlidePreview';

const sampleProductData: Record<string, any> = {
  name: 'Zaha Marsanne',
  vintage: '2021',
  price_net: '138.00',
  price_gross: '169.74',
  discount_price_net: '',
  discount_price_gross: '',
  brand: 'Telio',
  country: 'Argentyna',
  region: 'Dolina Uco, Tunuyán, Los Chacayes',
  volume: 750,
  alcohol_content: 12.5,
  description_short: 'jabłka i białych kwiatów, w tle nuty rumianku',
  description_long: 'Hołd winom doliny Rodanu oddany przez winiarzy - Alejandro Sejanovicha y Jeffa Mausbaca. Fermentacja w owalnych kadziach z cementu. Zredukowane dodawanie użycia tlenku siarki.',
  category: 'Wino białe',
  subcategory: 'wytrawne',
  characteristics: 'Dzięki fermentacji w kadzi cementowej i battonage',
  grape_variety: 'Marsanne',
  taste_profile: 'pomięciu fermentacji malolaktycznej czyste i kwasowe',
  image_url: '/media/20250606_203055_Obraz5.png',
};

// Simplified - removed parseProductVariables function as it's no longer needed

// Utility function to convert API template to frontend format
const apiToFrontendTemplate = (apiTemplate: any): TemplateConfig => {
  // Create a default welcome config
  const defaultWelcomeConfig = {
    title: 'Oferta dla {client_name}',
    subtitle: 'Przygotowana: {date}',
    showBrandHeader: false,
    brandHeaderText: 'WHISKY AND WINE PLACE',
    backgroundImage: '',
    logoUrl: '',
    showLogo: true,
    titleColor: '#000000',
    subtitleColor: '#555555',
    backgroundColor: '#ffffff',
    brandHeaderColor: '#8B4513',
    fontFamily: 'Book Antiqua',
    fontSize: 16
  };

  // Create a default product config
  const defaultProductConfig = {
    showPrice: true,
    showDiscount: false,
    backgroundUrl: '',
    backgroundColor: '#C8A982',
    titleColor: '#f8f9fa',
    detailsColor: '#f8f9va',
    descriptionColor: '#e9ecef',
    priceColor: '#f8f9fa',
    fontFamily: 'Arial',
    fontSize: 16,
    showAwards: false,
    closingFontSize: 18 // Add this with default value
  };

  // Merge the API data with defaults to ensure all properties exist
  return {
    id: apiTemplate.id,
    name: apiTemplate.name,
    description: apiTemplate.description,
    is_default: apiTemplate.is_default,
    created_at: apiTemplate.created_at,
    updated_at: apiTemplate.updated_at,
    welcome: { ...defaultWelcomeConfig, ...(apiTemplate.welcome_config || {}) },
    product: { ...defaultProductConfig, ...(apiTemplate.product_config || {}) }
  };
};

const TemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'welcome' | 'product' | 'closing'>('welcome');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Example structure for initial template state
  const initialTemplate: TemplateConfig = {
    name: 'Nowy Szablon',
    description: '',
    welcome: {
      title: 'Oferta dla {client_name}',
      subtitle: 'Przygotowana: {date}',
      showBrandHeader: false,
      brandHeaderText: 'WHISKY AND WINE PLACE',
      backgroundImage: '',
      logoUrl: '',
      showLogo: true,
      titleColor: '#000000',
      subtitleColor: '#555555',
      backgroundColor: '#ffffff',
      brandHeaderColor: '#8B4513',
      fontFamily: 'Book Antiqua',
      fontSize: 16,
    },
    product: {
      showPrice: true,
      showDiscount: false,
      backgroundUrl: '',
      backgroundColor: '#C8A982',
      titleColor: '#f8f9fa',
      detailsColor: '#f8f9fa',
      descriptionColor: '#e9ecef',
      priceColor: '#f8f9fa',
      fontFamily: 'Book Antiqua',
      fontSize: 16,
      showAwards: false,
      closingFontSize: 18 // Add this here too
    }
  };

  const [template, setTemplate] = useState<TemplateConfig>(initialTemplate);
  useEffect(() => {
    if (id) {
      fetchTemplate(parseInt(id));
    } else {
      setTemplate(initialTemplate);
    }
  }, [id]); // Remove initialTemplate from dependencies

  const fetchTemplate = async (templateId: number) => {
    setLoading(true);
    try {
      const response = await axios.get<TemplateApiResponse>(`/api/templates/${templateId}`);
      const apiTemplate = response.data;
      const frontendTemplate: TemplateConfig = apiToFrontendTemplate(apiTemplate);
      
      setTemplate(frontendTemplate);
      console.log('Loaded template with logo URL:', apiTemplate.welcome_config?.logoUrl);
    } catch (err: any) {
      setError('Błąd podczas ładowania szablonu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (field: keyof Omit<TemplateConfig, 'welcome' | 'product' | 'id' | 'is_default'>, value: string) => {
    setTemplate((prev: TemplateConfig) => ({ ...prev, [field]: value }));
  };

  const handleConfigChange = (section: 'welcome' | 'product', field: string, value: any) => {
    setTemplate(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const apiTemplate = {
        name: template.name,
        description: template.description,
        welcome_config: template.welcome,
        product_config: template.product,
        is_default: template.is_default || false
      };

      console.log('Saving template with logo URL:', template.welcome.logoUrl);

      if (template.id) {
        await axios.put(`/api/templates/${template.id}`, apiTemplate);
        setSuccess('Szablon został zaktualizowany pomyślnie!');
      } else {
        const response = await axios.post('/api/templates', apiTemplate);
        setTemplate(prev => ({ ...prev, id: response.data.id }));
        setSuccess('Szablon został utworzony pomyślnie!');
      }
    } catch (err: any) {
      setError('Błąd podczas zapisywania: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSetAsDefault = async () => {
    if (!template.id) {
      alert('Zapisz szablon przed ustawieniem go jako domyślny.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`/api/templates/${template.id}/set-default`);
      setSuccess('Szablon został ustawiony jako domyślny.');
      setError('');
      setTemplate((prev: TemplateConfig) => ({ ...prev, is_default: true }));
    } catch (err: any) {
      console.error('Error setting template as default:', err);
      setError('Wystąpił błąd podczas ustawiania szablonu jako domyślnego');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie szablonu...</div>;
  }

  return (
    <div className="container">
      <div className="card template-editor">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h1>{template.id ? 'Edytuj Szablon' : 'Nowy Szablon'}</h1>
          <div>
            <button 
              className="btn btn-outline-secondary me-2"
              onClick={() => navigate('/templates')}
            >
              Anuluj
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveTemplate}
              disabled={saving}
            >
              {saving ? 'Zapisywanie...' : 'Zapisz Szablon'}
            </button>
          </div>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-group mb-3">
            <label htmlFor="template-name">Nazwa szablonu</label>
            <input
              type="text"
              id="template-name"
              className="form-control"
              value={template.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleTemplateChange('name', e.target.value)}
              placeholder="Np. Szablon standardowy"
              required
            />
          </div>

          <div className="form-group mb-4">
            <label htmlFor="template-description">Opis (opcjonalnie)</label>
            <textarea
              id="template-description"
              className="form-control"
              value={template.description}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleTemplateChange('description', e.target.value)}
              rows={2}
            />
          </div>

          {template.id && !template.is_default && (
            <div className="mb-4">
              <button 
                className="btn btn-outline-primary"
                onClick={handleSetAsDefault}
              >
                Ustaw jako domyślny szablon
              </button>
            </div>
          )}

          {template.is_default && (
            <div className="alert alert-info mb-4">
              Ten szablon jest ustawiony jako domyślny.
            </div>
          )}

          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'welcome' ? 'active' : ''}`}
                href="#"
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  setActiveTab('welcome');
                }}
              >
                Slajd powitalny
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'product' ? 'active' : ''}`}
                href="#"
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  setActiveTab('product');
                }}
              >
                Slajd Produktowy
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeTab === 'closing' ? 'active' : ''}`}
                href="#"
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  setActiveTab('closing');
                }}
              >
                Slajd końcowy
              </a>
            </li>
          </ul>

          {/* Fix: Wrap tab content in a single container */}
          <div className="tab-content">
            {activeTab === 'welcome' && (
              <div key="welcome-tab-content" className="welcome-slide-config">
                <div className="row">
                  <div className="col-md-6">


                    <div className="form-group mb-3">
                      <label htmlFor="subtitle">Podtytuł</label>
                      <input
                        type="text"
                        id="subtitle"
                        className="form-control"
                        value={template.welcome.subtitle}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'subtitle', e.target.value)}
                        placeholder="np. Przygotowana: {date}"
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="backgroundImage">URL tła</label>
                      <input
                        type="url"
                        id="backgroundImage"
                        className="form-control"
                        value={template.welcome.backgroundImage || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'backgroundImage', e.target.value)}
                        placeholder="https://example.com/background.jpg"
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="logoUrl">URL Logo</label>
                      <input
                        type="url"
                        id="logoUrl"
                        className="form-control"
                        value={template.welcome.logoUrl || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'logoUrl', e.target.value)}
                        placeholder="https://example.com/logo.png"
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="backgroundColor">Kolor tła</label>
                      <input
                        type="color"
                        id="backgroundColor"
                        className="form-control form-control-color"
                        value={template.welcome.backgroundColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'backgroundColor', e.target.value)}
                      />
                    </div>


                    <div className="form-group mb-3">
                      <label htmlFor="subtitleColor">Kolor podtytułu</label>
                      <input
                        type="color"
                        id="subtitleColor"
                        className="form-control form-control-color"
                        value={template.welcome.subtitleColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'subtitleColor', e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="welcomeFontFamily">Rodzaj czcionki</label>
                      <select
                        id="welcomeFontFamily"
                        className="form-control"
                        value={template.welcome.fontFamily || 'Book Antiqua'}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => handleConfigChange('welcome', 'fontFamily', e.target.value)}
                      >
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Trebuchet MS">Trebuchet MS</option>
                        <option value="Book Antiqua">Book Antiqua</option>
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="welcomeFontSize">Wielkość czcionki (px)</label>
                      <input
                        type="number"
                        id="welcomeFontSize"
                        className="form-control"
                        min="8"
                        max="72"
                        value={template.welcome.fontSize || 16}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'fontSize', parseInt(e.target.value) || 16)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'product' && (
              <div key="product-tab-content" className="product-slide-config">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="showPrice"
                          className="form-check-input"
                          checked={template.product.showPrice}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'showPrice', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="showPrice">
                          Pokaż cenę
                        </label>
                      </div>
                    </div>


                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="showAwards"
                          className="form-check-input"
                          checked={template.product.showAwards}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'showAwards', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="showAwards">
                          Pokaż nagrody
                        </label>
                      </div>
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="backgroundUrl">URL tła</label>
                      <input
                        type="url"
                        id="backgroundUrl"
                        className="form-control"
                        value={template.product.backgroundUrl}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'backgroundUrl', e.target.value)}
                        placeholder="https://example.com/background.jpg"
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="productBackgroundColor">Kolor tła</label>
                      <input
                        type="color"
                        id="productBackgroundColor"
                        className="form-control form-control-color"
                        value={template.product.backgroundColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'backgroundColor', e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="fontFamily">Rodzaj czcionki</label>
                      <select
                        id="fontFamily"
                        className="form-control"
                        value={template.product.fontFamily}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => handleConfigChange('product', 'fontFamily', e.target.value)}
                      >
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Trebuchet MS">Trebuchet MS</option>
                        <option value="Book Antiqua">Book Antiqua</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="titleColor">Kolor tytułu</label>
                      <input
                        type="color"
                        id="titleColor"
                        className="form-control form-control-color"
                        value={template.product.titleColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'titleColor', e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="detailsColor">Kolor szczegółów</label>
                      <input
                        type="color"
                        id="detailsColor"
                        className="form-control form-control-color"
                        value={template.product.detailsColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'detailsColor', e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="descriptionColor">Kolor opisu</label>
                      <input
                        type="color"
                        id="descriptionColor"
                        className="form-control form-control-color"
                        value={template.product.descriptionColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'descriptionColor', e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="priceColor">Kolor ceny</label>
                      <input
                        type="color"
                        id="priceColor"
                        className="form-control form-control-color"
                        value={template.product.priceColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'priceColor', e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="fontSize">Wielkość czcionki (px)</label>
                      <input
                        type="number"
                        id="fontSize"
                        className="form-control"
                        min="8"
                        max="72"
                        value={template.product.fontSize}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'fontSize', parseInt(e.target.value) || 16)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'closing' && (
              <div key="closing-tab-content" className="closing-slide-config">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="closing-background-image">URL tła slajdu końcowego</label>
                      <input
                        type="url"
                        id="closing-background-image"
                        className="form-control"
                        value={template.welcome?.backgroundImage || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'backgroundImage', e.target.value)}
                        placeholder="https://example.com/closing-background.jpg"
                      />
                    </div>
                    
                    <div className="form-group mb-3">
                      <label htmlFor="closing-bg-color">Kolor tła</label>
                      <input
                        type="color"
                        id="closing-bg-color"
                        className="form-control form-control-color"
                        value={template.welcome?.backgroundColor || '#8B4513'}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('welcome', 'backgroundColor', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label htmlFor="closing-font-family">Rodzaj czcionki</label>
                      <select
                        id="closing-font-family"
                        className="form-control"
                        value={template.product?.fontFamily || 'Book Antiqua'}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => handleConfigChange('product', 'fontFamily', e.target.value)}
                      >
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Trebuchet MS">Trebuchet MS</option>
                        <option value="Book Antiqua">Book Antiqua</option>
                      </select>
                    </div>

                    <div className="form-group mb-3">
                      <label htmlFor="closing-font-size">Wielkość czcionki slajdu końcowego (px)</label>
                      <input
                        type="number"
                        id="closing-font-size"
                        className="form-control"
                        min="8"
                        max="72"
                        value={template.product?.closingFontSize || 18}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleConfigChange('product', 'closingFontSize', parseInt(e.target.value) || 18)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="preview-section">
            <h3>Podgląd slajdu</h3>
            <SlidePreview 
              template={template} 
              previewType={activeTab as 'welcome' | 'product' | 'closing'} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;